<template>
  <ul
    v-if="paginate.last_page > 1"
    class="flex justify-center pl-0 list-none rounded my-2">
    <li
      :class="{ 'bg-gray-200 pointer-events-none': paginate.current_page === 1, 'cursor-pointer' : paginate.current_page !== 1 }"
      class="relative block py-2 px-3 leading-tight bg-white border border-gray-300 text-blue-700 border-r-0 ml-0 rounded-l hover:bg-gray-200">
      <span
        class="page-link"
        @click="$emit('page-previous', paginate.current_page - 1)">
        前へ
      </span>
    </li>
    <li
      v-for="page in visiblePages"
      :key="page.key"
      :class="pageClass(page)"
      class="relative block py-2 px-3 leading-tight bg-white border border-gray-300 border-r-0"
      @click="onPageClicked(page)">
      <span
        class="page-link">
        {{ page.label }}
      </span>
    </li>
    <li
      :class="{ 'bg-gray-200 pointer-events-none': paginate.current_page >= paginate.last_page, 'cursor-pointer' : paginate.current_page < paginate.last_page }"
      class="relative block py-2 px-3 leading-tight bg-white border border-gray-300 text-blue-700 rounded-r hover:bg-gray-200"
      @click="$emit('page-next', paginate.current_page + 1)">
      <span
        class="page-link">
        次へ
      </span>
    </li>
  </ul>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  paginate: {
    type: Object,
    default: () => ({
      total: 0,
      per_page: 0,
      current_page: 0,
      last_page: 0,
      from: 0,
      to: 0,
      data: [],
    }),
  },
})

const emit = defineEmits(['page-click', 'page-next', 'page-previous'])

const calRange = (start, end) => {
  const range = []
  for (let i = start; i <= end; i += 1) {
    range.push(i)
  }
  return range
}

const visiblePages = computed(() => {
  const lastPage = props.paginate.last_page
  const currentPage = props.paginate.current_page

  if (lastPage <= 7) {
    return calRange(1, lastPage).map(page => ({
      key: `page-${page}`,
      label: page,
      page,
      isDot: false,
    }))
  }

  const pages = new Set([
    1,
    2,
    lastPage - 1,
    lastPage,
  ])

  calRange(currentPage - 2, currentPage + 2).forEach(page => {
    if (page >= 1 && page <= lastPage) {
      pages.add(page)
    }
  })

  return [...pages]
    .sort((a, b) => a - b)
    .reduce((items, page) => {
      const previousItem = items[items.length - 1]
      if (previousItem && !previousItem.isDot && page - previousItem.page > 1) {
        items.push({
          key: `dot-${previousItem.page}-${page}`,
          label: '...',
          page: null,
          isDot: true,
        })
      }
      items.push({
        key: `page-${page}`,
        label: page,
        page,
        isDot: false,
      })
      return items
    }, [])
})

const pageClass = (page) => {
  if (page.isDot) {
    return 'disabled'
  }

  return {
    'bg-blue-500 text-white': page.page === props.paginate.current_page,
    'text-blue-700 hover:bg-gray-200 cursor-pointer': page.page !== props.paginate.current_page,
  }
}

const onPageClicked = (page) => {
  if (!page.isDot) {
    emit('page-click', page.page)
  }
}
</script>

<style scoped>
.disabled {
  cursor: not-allowed;
}
</style>
